import { DictionaryRecordContextProvider } from "@/components/dictionaries/DictionaryContext";
import DictionaryForm from "@/components/dictionaries/DictionaryForm";
import Field from "@/components/Field";
import Fieldset from "@/components/Fieldset";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { useToast } from "@/components/ui/use-toast";
import { isRequired } from "@/lib/form";
import { isNotId } from "@/lib/utils/isNotId";
import { useStoreGroupDictionary } from "@/modules/storeGroups/hooks/useStoreGroupDictionary";
import { displayStoreGroup } from "@/schemas/storeGroup";
import { WriteStoreGroup, ReadStoreGroup } from "@/types/storeGroup";
import { ApiError } from "@/services/api";
import { zodResolver } from "@hookform/resolvers/zod";
import { MutationFunction, useMutation } from "@tanstack/react-query";
import { exists } from "i18next";
import { Info } from "lucide-react";
import { PropsWithChildren, useCallback, useState } from "react";
import { DefaultValues, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Module } from "@/constants";
import { useQueryCacheUpdater } from "@/hooks/useQueryCacheUpdater";
import SaveButton from "@/components/SaveButton";
import { useModulePermissions } from "@/modules/account/hooks/useModulePermissions";

export default function StoreGroupCreator({ children }: PropsWithChildren) {
  const id = "new";
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const config = useStoreGroupDictionary();
  const labels = config.getLabels(t);
  const { Icon, ns } = config;
  const { invalidateListQuery } = useQueryCacheUpdater(Module.StoreGroups);
  const { can_create } = useModulePermissions();
  const mutationFn: MutationFunction<ReadStoreGroup, WriteStoreGroup> = async (
    values,
  ) => {
    const response = await config.service.post({
      name: values.name,
      description: values.description,
    });

    return response;
  };

  const mutation = useMutation<ReadStoreGroup, ApiError, WriteStoreGroup>({
    mutationFn,
    onSuccess: (response) => {
      const id = response.id;
      navigate(`${config.path}/${id}`);
      toast({
        title: labels.success.create,
      });
      invalidateListQuery();
    },
    onError: ({ errorCode }) => {
      const description = exists(`errors.${errorCode}`)
        ? t(`errors.${errorCode}`)
        : t("errors.unknownError");
      toast({
        title: labels.errors.create,
        description,
        variant: "destructive",
      });
    },
  });

  const form = useForm<WriteStoreGroup>({
    resolver: zodResolver(config.schema),
    defaultValues: (config.defaultValues ||
      {}) as DefaultValues<WriteStoreGroup>,
    disabled: mutation.isPending || !can_create,
  });

  const onOpenChange = useCallback(
    (open: boolean) => {
      if (!open) {
        form.reset();
      }
      setOpen(open);
    },
    [form],
  );

  return (
    <DictionaryRecordContextProvider value={{ id, form, mutation, config }}>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogTrigger asChild>{children}</DialogTrigger>
        {open && (
          <DialogContent className="w-4/5 max-w-xl">
            <DictionaryForm>
              <DialogHeader>
                <DialogTitle className="flex flex-row gap-2 items-center">
                  <Icon />
                  {t("storeGroups.dialog.title")}
                </DialogTitle>
              </DialogHeader>
              <Fieldset className="gap-4">
                {Object.entries(displayStoreGroup.unwrap().shape)
                  .filter(([key]) => isNotId(key))
                  .map(([key, field], index) => (
                    <Field
                      key={index}
                      name={key}
                      label={t(`${ns}.fields.${key}`)}
                      required={isRequired(field)}
                      render={({ field }) => (
                        <Input
                          disabled={mutation.isPending}
                          className="w-full"
                          {...field}
                        />
                      )}
                    />
                  ))}
              </Fieldset>

              <div className="flex items-center gap-2 text-sm text-slate-600">
                <Info className="w-4 h-4" />
                <span>{t("storeGroups.dialog.info")}</span>
              </div>
              <DialogFooter className="gap-3 mt-3">
                <DialogClose asChild>
                  <Button variant="link" disabled={mutation.isPending}>
                    {t("storeGroups.dialog.cancel")}
                  </Button>
                </DialogClose>
                <SaveButton />
              </DialogFooter>
            </DictionaryForm>
          </DialogContent>
        )}
      </Dialog>
    </DictionaryRecordContextProvider>
  );
}
